// Shared UI primitives — ported from
// design/design_handoff_nifty_bull/prototype/ui.jsx and adapted to take
// the strategy list from props (loaded from /api/strategies) instead of
// the prototype's hardcoded STRATEGIES global.

const Icon = {
    Search: (p) => (<svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="9" cy="9" r="6" /><path d="m17 17-3.5-3.5" /></svg>),
    Chevron: (p) => (<svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="m6 8 4 4 4-4" /></svg>),
    Close: (p) => (<svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="m5 5 10 10M15 5 5 15" /></svg>),
    Play: (p) => (<svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor" {...p}><path d="M6 4.5v11a.5.5 0 0 0 .77.42l8.4-5.5a.5.5 0 0 0 0-.84l-8.4-5.5A.5.5 0 0 0 6 4.5Z" /></svg>),
    Back: (p) => (<svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M16 10H4m4 4-4-4 4-4" /></svg>),
    Info: (p) => (<svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="10" cy="10" r="7" /><path d="M10 9v5M10 6.5v.01" /></svg>),
    Bot: (p) => (<svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" {...p}><rect x="4" y="7" width="12" height="9" rx="2.5" /><path d="M10 4v3M8 11v.01M12 11v.01M2 12h2M16 12h2" /></svg>),
    Check: (p) => (<svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="m5 10 3.5 3.5L15 7" /></svg>),
    Download: (p) => (<svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M10 3v10m0 0 4-4m-4 4-4-4M4 16h12" /></svg>),
};

// ---------- Formatters ----------
// Per canonical web-interface-guidelines #66/#67: route currency, date,
// and number formatting through the Intl API instead of hand-rolled
// strings. The locale is fixed to en-IN so the lakhs/crores grouping
// (e.g. ₹5,00,000) and the dd MMM yyyy date format apply regardless
// of the user's browser locale — this app is NSE-only and the design
// system (₹ prefix, two-decimal prices) is calibrated to that.

// Lazily-initialised module-level formatters. Building NumberFormat /
// DateTimeFormat is non-trivial; cache them so we're not paying that
// cost on every render of every KPI tile.
const _inrInt = new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR',
    maximumFractionDigits: 0, minimumFractionDigits: 0,
});
const _inrDecCache = new Map();
const _inrFor = (decimals) => {
    let f = _inrDecCache.get(decimals);
    if (!f) {
        f = new Intl.NumberFormat('en-IN', {
            style: 'currency', currency: 'INR',
            maximumFractionDigits: decimals, minimumFractionDigits: decimals,
        });
        _inrDecCache.set(decimals, f);
    }
    return f;
};
const _dateLong = new Intl.DateTimeFormat('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
});
const _dateShort = new Intl.DateTimeFormat('en-IN', {
    day: '2-digit', month: 'short',
});

// Compact lakhs/crores. Intl's `notation: 'compact'` with locale en-IN
// emits "5L" / "2Cr" natively, but the design uses "₹5.00L" with the
// ₹ glyph and a two-decimal precision the Intl compact path doesn't
// expose, so we keep the manual division for the compact branch and
// only delegate the un-compact path to Intl. Same observable output
// as before — just routed through Intl for digits + grouping.
const fmtINR = (n, opts = {}) => {
    const { compact = false, decimals = 0 } = opts;
    if (n == null || isNaN(n)) return '—';
    if (compact) {
        if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
        if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
        if (Math.abs(n) >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
    }
    return decimals === 0 ? _inrInt.format(n) : _inrFor(decimals).format(n);
};

const fmtDate = (d, short = false) => {
    if (!(d instanceof Date)) d = new Date(d);
    return (short ? _dateShort : _dateLong).format(d);
};

const fmtPct = (n, decimals = 2) => `${n >= 0 ? '+' : ''}${n.toFixed(decimals)}%`;

// ---------- Hint (hover-tooltip) ----------
// Renders a small info icon; on hover, shows a popover with the
// supplied content. The popup is portaled to document.body and
// positioned via fixed coords from getBoundingClientRect, so it
// escapes any overflow:hidden ancestor (e.g. the trades-table card
// that intentionally clips its inner scroll area).
function Hint({ children, width = 280 }) {
    const [open, setOpen] = React.useState(false);
    const [pos, setPos] = React.useState({ top: 0, left: 0, flip: false, anchorRight: false });
    const iconRef = React.useRef(null);

    const reposition = React.useCallback(() => {
        if (!iconRef.current) return;
        const rect = iconRef.current.getBoundingClientRect();
        // Estimate popup size — actual height varies with copy length
        // and the user's font settings, but 160px covers our tooltips.
        const estHeight = 160;
        const flip = rect.top < estHeight + 16; // below if no room above
        const anchorRight = rect.left + width + 16 > window.innerWidth;
        setPos({
            top: flip ? rect.bottom + 8 : rect.top - 8,
            left: anchorRight ? rect.right : rect.left,
            flip,
            anchorRight,
        });
    }, [width]);

    const onEnter = () => { reposition(); setOpen(true); };
    const onLeave = () => setOpen(false);

    return (
        <span ref={iconRef} className="hint"
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
            onFocus={onEnter}
            onBlur={onLeave}
            tabIndex={0}>
            <Icon.Info width="13" height="13" />
            {open && ReactDOM.createPortal(
                <span className={`hint-popup${pos.flip ? ' flipped' : ''}${pos.anchorRight ? ' anchor-right' : ''}`}
                    style={{
                        position: 'fixed',
                        top: pos.top,
                        left: pos.anchorRight ? 'auto' : pos.left,
                        right: pos.anchorRight ? (window.innerWidth - pos.left) : 'auto',
                        transform: pos.flip ? 'none' : 'translateY(-100%)',
                        width,
                    }}>
                    {children}
                </span>,
                document.body
            )}
        </span>
    );
}

// ---------- Strategy Picker ----------
// Strategy archetype illustration. Returns the matching PNG when the
// asset exists for the strategy id; falls back to a 2-letter monogram
// chip when it doesn't (so a freshly-added strategy doesn't crash the
// picker just because the artwork hasn't shipped yet).
function StrategyArt({ strategy, size = 32, monogramBg = 'var(--accent-soft)', monogramFg = 'var(--accent-text)' }) {
    const [failed, setFailed] = React.useState(false);
    const src = `./assets/strategies/${strategy.id}.png`;
    if (failed) {
        // Monogram fallback — keeps the UI graceful if an asset is
        // missing for a newly-added strategy.
        return (
            <div style={{
                width: size, height: size, borderRadius: 'var(--radius-sm)',
                background: monogramBg, color: monogramFg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: `${Math.round(size * 0.36)}px`, fontWeight: 600,
                letterSpacing: 'var(--tracking-label)', flex: 'none',
            }}>
                {strategy.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}
            </div>
        );
    }
    return (
        <img
            src={src} alt="" width={size} height={size}
            loading="lazy" decoding="async" draggable={false}
            onError={() => setFailed(true)}
            style={{
                width: size, height: size,
                objectFit: 'contain',
                // No fill behind the art — the PNG was alpha-keyed
                // by tooling/remove-bg.py so the canvas (whatever
                // colour) shows through naturally.
                flex: 'none', userSelect: 'none', pointerEvents: 'none',
            }}
        />
    );
}

function StrategyPicker({ strategies, value, onChange }) {
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef(null);
    const selected = strategies.find((s) => s.id === value) || strategies[0];

    React.useEffect(() => {
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    if (!selected) return null;

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                style={{
                    width: '100%', textAlign: 'left',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: 'var(--space-3) var(--space-4)',
                    background: 'var(--surface)',
                    border: `1px solid ${open ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: 'var(--radius)',
                    boxShadow: open ? '0 0 0 3px var(--accent-ring)' : 'var(--shadow-xs)',
                    cursor: 'pointer',
                    transition: 'all var(--motion-fast) var(--ease-out)',
                    font: 'inherit', color: 'inherit',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <StrategyArt strategy={selected} size={36} />
                    <div>
                        <div style={{ fontSize: 'var(--text-md)', fontWeight: 550, letterSpacing: 'var(--tracking-base)' }}>{selected.name}</div>
                        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-3)', marginTop: 'var(--space-1)' }}>{selected.category} · {selected.complexity}</div>
                    </div>
                </div>
                <div style={{ color: 'var(--text-3)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform var(--motion-fast) var(--ease-out)' }}><Icon.Chevron /></div>
            </button>

            {open && (
                <div className="slide-up" style={{
                    position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-lg)',
                    boxShadow: 'var(--shadow-lg)',
                    zIndex: 20,
                    maxHeight: 420, overflow: 'auto',
                    padding: 'var(--space-2)',
                }}>
                    {strategies.map((s) => {
                        const active = s.id === value;
                        return (
                            <button key={s.id} type="button"
                                onClick={() => { onChange(s.id); setOpen(false); }}
                                style={{
                                    width: '100%', textAlign: 'left',
                                    display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                                    padding: 'var(--space-3) var(--space-3)', border: 0,
                                    background: active ? 'var(--accent-softer)' : 'transparent',
                                    borderRadius: 'var(--radius-sm)',
                                    cursor: 'pointer', font: 'inherit', color: 'inherit',
                                    transition: 'background var(--motion-fast) var(--ease-out)',
                                }}
                                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'var(--surface-2)'; }}
                                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                            >
                                <div style={{ position: 'relative', flex: 'none' }}>
                                    <StrategyArt strategy={s} size={32} />
                                    {active && (
                                        <div style={{
                                            position: 'absolute', right: -4, bottom: -4,
                                            width: 16, height: 16, borderRadius: '50%',
                                            background: 'var(--accent)', color: 'white',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            boxShadow: 'var(--shadow-selected)',
                                            border: '2px solid var(--surface)',
                                        }}>
                                            <svg viewBox="0 0 12 12" width="8" height="8" fill="none"
                                                 stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="m3 6 2 2 4-4" />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 'var(--text-base)', fontWeight: 550, color: active ? 'var(--accent-text)' : 'var(--text)' }}>{s.name}</div>
                                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-3)', marginTop: 'var(--space-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {s.short}
                                    </div>
                                </div>
                                <span className="pill" style={{ fontSize: 'var(--text-xs)', padding: '3px 7px', flex: 'none' }}>{s.complexity}</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ---------- Strategy Details Modal ----------

// Build a single regex from glossary keys so the auto-linker matches
// the longest term first ("RSI(2)" before "RSI"). Memoised per-glossary
// so we don't recompile on every render.
const _glossaryRegexCache = new WeakMap();
function buildGlossaryRegex(glossary) {
    if (!glossary || typeof glossary !== 'object') return null;
    const cached = _glossaryRegexCache.get(glossary);
    if (cached !== undefined) return cached;
    const keys = Object.keys(glossary);
    if (keys.length === 0) {
        _glossaryRegexCache.set(glossary, null);
        return null;
    }
    // Escape regex metachars in each key — parentheses are common
    // (e.g. "RSI(2)") and would otherwise be interpreted as groups.
    const escaped = keys.map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const re = new RegExp(`(${escaped.join('|')})`, 'g');
    _glossaryRegexCache.set(glossary, re);
    return re;
}

// Replace every occurrence of a glossary term inside `text` with a
// React <a> element. Returns an array of strings + JSX nodes ready
// to be rendered as the children of any element. Falls back to the
// raw string if the glossary is empty.
function autoLinkGlossary(text, glossary) {
    const re = buildGlossaryRegex(glossary);
    if (!re || !text) return text;
    const parts = [];
    let lastIndex = 0;
    let match;
    re.lastIndex = 0;
    while ((match = re.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }
        const term = match[1];
        const url = glossary[term];
        parts.push(
            <a
                key={`gl-${match.index}`}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--accent, #2b6cb0)', borderBottom: '1px dotted currentColor', textDecoration: 'none' }}
            >
                {term}
            </a>
        );
        lastIndex = match.index + term.length;
    }
    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }
    return parts;
}

function StrategyModal({ strategy, glossary, onClose }) {
    if (!strategy) return null;
    const s = strategy;
    const refs = Array.isArray(s.references) ? s.references : [];
    const tweaks = Array.isArray(s.tweaks) ? s.tweaks : [];
    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
                <div style={{ padding: 'var(--space-6) var(--space-7) var(--space-5)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--space-4)' }}>
                    <div>
                        <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
                            <span className="pill accent">{s.category}</span>
                            <span className="pill">{s.complexity}</span>
                        </div>
                        <h2 style={{ margin: 0, fontSize: 'var(--text-2xl)', fontWeight: 600, letterSpacing: 'var(--tracking-display)' }}>{s.name}</h2>
                        <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--text-2)', fontSize: 'var(--text-md)' }}>{s.short}</p>
                    </div>
                    <button className="btn ghost" onClick={onClose} aria-label="Close" style={{ padding: 'var(--space-2)' }}><Icon.Close /></button>
                </div>
                <hr className="hr" />
                <div style={{ padding: 'var(--space-5) var(--space-7) var(--space-3)' }}>
                    <div className="label">How it works</div>
                    <p style={{ margin: 0, lineHeight: 1.65, fontSize: 'var(--text-md)', color: 'var(--text)' }}>
                        {autoLinkGlossary(s.description, glossary)}
                    </p>
                </div>
                {tweaks.length > 0 && (
                    <div style={{ padding: 'var(--space-3) var(--space-7) var(--space-3)' }}>
                        <div className="label">Our adjustments</div>
                        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.65, fontSize: 'var(--text-md)', color: 'var(--text)' }}>
                            {tweaks.map((t, i) => (
                                <li key={i} style={{ marginBottom: 4 }}>{autoLinkGlossary(t, glossary)}</li>
                            ))}
                        </ul>
                    </div>
                )}
                {s.eli5 && (
                    <div style={{ padding: 'var(--space-3) var(--space-7) var(--space-3)' }}>
                        <div className="label">Explain like I'm five</div>
                        <p style={{ margin: 0, lineHeight: 1.65, fontSize: 'var(--text-md)', color: 'var(--text-2)', fontStyle: 'italic' }}>
                            {s.eli5}
                        </p>
                    </div>
                )}
                <div style={{ padding: 'var(--space-5) var(--space-7)', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--space-3)' }}>
                    <MetricTile label="Historical win rate" value={`${s.metrics.winRate}%`} />
                    <MetricTile label="Trade frequency" value={s.metrics.trades} />
                    <MetricTile label="Horizon" value={s.metrics.horizon} />
                </div>
                {refs.length > 0 && (
                    <div style={{ padding: 'var(--space-3) var(--space-7) var(--space-5)' }}>
                        <div className="label">References</div>
                        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                            {refs.map((r, i) => (
                                <li key={i} style={{ marginBottom: 'var(--space-2)' }}>
                                    <a
                                        href={r.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: 'var(--accent, #2b6cb0)', textDecoration: 'none', fontSize: 'var(--text-md)', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                    >
                                        {r.label}
                                        <span aria-hidden="true" style={{ fontSize: 12, opacity: 0.7 }}>↗</span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                <div style={{ padding: '0 var(--space-7) var(--space-6)' }}>
                    <div style={{
                        background: 'var(--bg-2)', borderRadius: 'var(--radius)', padding: 'var(--space-4)',
                        fontSize: 'var(--text-sm)', color: 'var(--text-2)', lineHeight: 1.55,
                        border: '1px solid var(--border)',
                    }}>
                        <strong style={{ color: 'var(--text)' }}>Note —</strong> Historical metrics are illustrative,
                        computed on NIFTY 200 constituents. Live performance will vary with market regime,
                        slippage, and position sizing.
                    </div>
                </div>
            </div>
        </div>
    );
}

function MetricTile({ label, value }) {
    return (
        <div style={{
            background: 'var(--bg-2)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', padding: 'var(--space-3) var(--space-4)',
        }}>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-3)', letterSpacing: 'var(--tracking-label)', textTransform: 'uppercase' }}>{label}</div>
            <div className="mono" style={{ fontSize: 'var(--text-lg)', fontWeight: 600, marginTop: 'var(--space-1)', letterSpacing: 'var(--tracking-heading)' }}>{value}</div>
        </div>
    );
}

// ---------- Running Backtest overlay ----------
function BacktestRunning({ strategy, jobStatus }) {
    const steps = [
        'Fetching historical NSE data',
        'Computing indicators & signals',
        'Simulating trades with slippage',
        'Compiling portfolio metrics',
    ];

    // Drive a smooth client-side progress bar regardless of server polling
    // cadence — the server reports QUEUED → RUNNING → COMPLETED, but the
    // user wants to see continuous motion. The bar speeds toward each
    // milestone but never claims completion until the server says so.
    const [progress, setProgress] = React.useState(0);
    React.useEffect(() => {
        if (!jobStatus) return;
        const target = jobStatus === 'COMPLETED' ? 1
            : jobStatus === 'RUNNING' ? 0.92
            : jobStatus === 'QUEUED' ? 0.18
            : 0.5;
        const interval = setInterval(() => {
            setProgress((p) => {
                if (p >= target) return p;
                return Math.min(target, p + 0.012);
            });
        }, 60);
        return () => clearInterval(interval);
    }, [jobStatus]);

    const step = Math.min(steps.length - 1, Math.floor(progress * steps.length));

    return (
        <div style={{
            position: 'fixed', inset: 0,
            background: 'oklch(0.965 0.016 80 / 0.78)',
            backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 50,
            animation: 'fade-in var(--motion-base) var(--ease-out) both',
        }}>
            <div style={{
                width: 440, background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-xl)',
                boxShadow: 'var(--shadow-lg)',
                padding: 'var(--space-7)',
            }}>
                <div style={{ textAlign: 'center', marginBottom: 'var(--space-5)' }}>
                    {/* Running bull — designed PNG illustration with a
                        gentle float loop. Replaces the static bot icon
                        for a more characterful "we're working on it"
                        cue. Falls back to the bot icon if FX hasn't
                        loaded (defensive — index.html load order
                        means FX is in scope by the time this renders). */}
                    {window.FX && window.FX.BullRunning ? (
                        <div className="bull-float" style={{ display: 'inline-block' }}>
                            <window.FX.BullRunning width={220} />
                        </div>
                    ) : <Icon.Bot />}
                    <div className="eyebrow mt-3">Running backtest</div>
                    <div style={{
                        fontFamily: 'var(--font-display)',
                        fontVariationSettings: "'opsz' 72",
                        fontSize: 'var(--text-xl)',
                        fontWeight: 600,
                        letterSpacing: 'var(--tracking-heading)',
                        color: 'var(--text)',
                        marginTop: 'var(--space-2)',
                    }}>{strategy?.name || 'Strategy'}</div>
                </div>

                <div style={{ height: 6, background: 'var(--surface-3)', borderRadius: 'var(--radius-pill)', overflow: 'hidden', marginBottom: 'var(--space-6)' }}>
                    <div style={{
                        width: `${progress * 100}%`, height: '100%',
                        background: 'var(--accent)',
                        borderRadius: 'var(--radius-pill)',
                        transition: 'width var(--motion-fast) linear',
                    }} />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                    {steps.map((label, i) => {
                        const done = i < step;
                        const active = i === step;
                        return (
                            <div key={i} style={{
                                display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                                opacity: active || done ? 1 : 0.42,
                                transition: 'opacity var(--motion-base) var(--ease-out)',
                            }}>
                                <div style={{
                                    width: 20, height: 20, borderRadius: '50%',
                                    background: done ? 'var(--accent)' : active ? 'var(--accent-soft)' : 'var(--surface-3)',
                                    color: done ? 'white' : 'var(--accent)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 'var(--text-xs)',
                                    transition: 'all var(--motion-base) var(--ease-out)',
                                    boxShadow: done ? 'var(--shadow-sm)' : 'none',
                                }}>
                                    {done ? <Icon.Check /> : active ? <span className="dots" style={{ transform: 'scale(0.55)' }}><span /><span /><span /></span> : ''}
                                </div>
                                <div style={{
                                    fontSize: 'var(--text-base)',
                                    color: active ? 'var(--text)' : done ? 'var(--text-2)' : 'var(--text-3)',
                                    fontWeight: active ? 550 : 400,
                                }}>
                                    {label}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// ---------- Animated numeric ----------
// Counts up from 0 to the value parsed out of `text` over ~900ms with
// an ease-out curve, rebuilding the formatted string each frame so
// suffixes like "%" and "x" stay attached. Skips entirely when the
// user has prefers-reduced-motion set, or when the value can't be
// parsed (e.g. "—", "Strong").
//
// Re-runs only when the parsed numeric target changes; same-value
// re-renders (parent rebuilds for unrelated reasons) keep the final
// string. Match: optional sign, digits, optional `.frac`, suffix.
const _NUM_RE = /^([+\-]?)(\d+)(?:\.(\d+))?(.*)$/;
function _parseNumeric(text) {
    if (typeof text !== 'string') return null;
    const m = _NUM_RE.exec(text);
    if (!m) return null;
    const [, sign, whole, frac = '', suffix = ''] = m;
    const value = parseFloat(`${whole}${frac ? '.' + frac : ''}`);
    if (!isFinite(value)) return null;
    return { prefix: sign, value, suffix, fractionDigits: frac.length };
}

function AnimatedNumeric({ text, className, style }) {
    const parsed = React.useMemo(() => _parseNumeric(text), [text]);
    const reduceMotion = React.useMemo(
        () => typeof window !== 'undefined'
            && window.matchMedia
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
        []);
    const [display, setDisplay] = React.useState(() =>
        parsed && !reduceMotion ? `${parsed.prefix}${(0).toFixed(parsed.fractionDigits)}${parsed.suffix}` : text);

    React.useEffect(() => {
        if (!parsed || reduceMotion) { setDisplay(text); return; }
        const start = performance.now();
        const dur = 900;
        let raf = 0;
        const tick = (now) => {
            const t = Math.min(1, (now - start) / dur);
            // Ease-out cubic — matches the Flutter Curves.easeOutCubic
            // and the design system's --ease-out compositor curve.
            const eased = 1 - Math.pow(1 - t, 3);
            const v = parsed.value * eased;
            setDisplay(`${parsed.prefix}${v.toFixed(parsed.fractionDigits)}${parsed.suffix}`);
            if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [parsed, text, reduceMotion]);

    return <span className={className} style={style}>{display}</span>;
}

// ---------- KPI Tile ----------
// Editorial KPI — Fraunces (variable serif, opsz 144) for the
// numeric, all-caps Schibsted eyebrow for the label, optional
// `featured` rail for the headline tile. The whole tile blooms in
// on first render via the .kpi-bloom keyframe so a fresh dashboard
// settles instead of snap-popping.
function KPITile({ label, value, delta, deltaLabel, good, highlight, hint, stellar }) {
    const up = good === 'up';
    const down = good === 'down';
    // `stellar` = "this number is genuinely impressive" — used by the
    // dashboard to flag CAGR > 20% etc. Adds a subtle aurora ring
    // behind the tile (see .kpi-stellar in style.css).
    const baseClass = highlight ? 'card featured kpi-bloom lift kpi-tile' : 'card kpi-bloom lift kpi-tile';
    const cardClass = stellar ? `${baseClass} kpi-stellar` : baseClass;
    // Fall back to the count-up AnimatedNumeric if the FX module
    // hasn't loaded yet (load-order safety; index.html declares
    // celebrations.jsx before app.jsx so this is a belt-and-braces
    // guard, not a hot path).
    const Numeric = (typeof window !== 'undefined' && window.FX && window.FX.DigitRollNumeric) || AnimatedNumeric;
    return (
        <div className={cardClass}>
            <div className="kpi-eyebrow row gap-2">
                {label}
                {hint && <Hint>{hint}</Hint>}
            </div>
            <Numeric
                text={value}
                className="tnum kpi-value"
                style={{
                    color: highlight ? 'var(--accent-text)' : 'var(--text)',
                }} />
            <div style={{ display: 'none' }}>{value}</div>
            {delta != null && (
                <div className="kpi-delta">
                    <span className={`pill ${up ? 'up' : down ? 'down' : ''}`}>
                        {delta}
                    </span>
                    {deltaLabel && <span className="t-sm tx-3">{deltaLabel}</span>}
                </div>
            )}
        </div>
    );
}

// Company logo chip — used in the trades + holdings row leading
// avatar slot. Tries to load assets/companies/{TICKER}.png; falls
// back to a 2-letter monogram chip if the image is missing or 404s
// (the registry is curated; new NIFTY 100 entrants ship without
// logos until tooling/fetch-company-logos.py is re-run).
function CompanyLogo({ symbol, size = 28 }) {
    const [failed, setFailed] = React.useState(false);
    const initials = (symbol || '??').replace(/[-&]/g, '').slice(0, 2);
    if (failed || !symbol) {
        return (
            <div className="row center mono fw-600 tx-2 flex-none" translate="no" style={{
                width: size, height: size, borderRadius: 'var(--radius-sm)',
                background: 'var(--surface-2)',
                fontSize: 'var(--text-xs)',
            }}>{initials}</div>
        );
    }
    return (
        <img
            src={`./assets/companies/${encodeURIComponent(symbol)}.png`}
            width={size} height={size} alt=""
            loading="lazy" decoding="async" draggable={false}
            onError={() => setFailed(true)}
            className="flex-none"
            style={{
                width: size, height: size,
                borderRadius: 'var(--radius-sm)',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                objectFit: 'contain',
                padding: 2,
            }}
        />
    );
}

window.UI = { Icon, fmtINR, fmtDate, fmtPct, StrategyPicker, StrategyArt, CompanyLogo, StrategyModal, MetricTile, BacktestRunning, KPITile, Hint, AnimatedNumeric };
